import React, { createContext, useContext, useEffect, useState } from "react";
import keycloak, { KEYCLOAK_REDIRECT_URI } from "../../lib/keycloak";

const KeycloakContext = createContext(undefined);

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (context === undefined) {
    throw new Error("useKeycloak must be used within a KeycloakProvider");
  }
  return context;
};

export const KeycloakProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const initKeycloak = async () => {
      try {
        // Check if we're in OAuth callback (has tokens in URL hash)
        const hash = window.location.hash;
        const isCallback = hash && 
                          (hash.includes('access_token') || 
                           hash.includes('code=') ||
                           hash.includes('state='));
        
        const auth = await keycloak.init({
          // Never use check-sso to prevent auto-login from Keycloak SSO session
          // Users must explicitly click login button
          onLoad: isCallback ? 'check-sso' : undefined,
          checkLoginIframe: false,
          enableLogging: false,
          pkceMethod: 'S256',
          responseMode: 'fragment',
          flow: 'standard',
        });

        // If authenticated, ensure tokens are in localStorage
        if (auth && keycloak.token) {
          localStorage.setItem('keycloak_token', keycloak.token);
          localStorage.setItem('keycloak_authenticated', 'true');
          if (keycloak.refreshToken) {
            localStorage.setItem('keycloak_refresh_token', keycloak.refreshToken);
          }
          // Set user info
          if (keycloak.tokenParsed) {
            setUser({
              username: keycloak.tokenParsed.preferred_username || 'unknown',
              email: keycloak.tokenParsed.email || '',
              name: keycloak.tokenParsed.name || '',
            });
          }
        }

        setInitialized(true);
        setAuthenticated(auth);
        setToken(keycloak.token || null);
      } catch (error) {
        setInitialized(true);
        setAuthenticated(false);
      }
    };

    initKeycloak();

    // Setup event listeners for React state updates
    keycloak.onAuthSuccess = () => {
      setAuthenticated(true);
      setToken(keycloak.token || null);
      
      // Save tokens to localStorage for API calls
      if (keycloak.token) {
        localStorage.setItem('keycloak_token', keycloak.token);
        localStorage.setItem('keycloak_authenticated', 'true');
        if (keycloak.refreshToken) {
          localStorage.setItem('keycloak_refresh_token', keycloak.refreshToken);
        }
      }
      
      if (keycloak.tokenParsed) {
        setUser({
          username: keycloak.tokenParsed.preferred_username || 'unknown',
          email: keycloak.tokenParsed.email || '',
          name: keycloak.tokenParsed.name || '',
        });
      }
    };

    keycloak.onAuthRefreshSuccess = () => {
      setToken(keycloak.token || null);
      
      // Update tokens in localStorage
      if (keycloak.token) {
        localStorage.setItem('keycloak_token', keycloak.token);
        if (keycloak.refreshToken) {
          localStorage.setItem('keycloak_refresh_token', keycloak.refreshToken);
        }
      }
    };

    keycloak.onAuthLogout = () => {
      setAuthenticated(false);
      setToken(null);
      setUser(null);
      
      // Clear tokens from localStorage
      localStorage.removeItem('keycloak_token');
      localStorage.removeItem('keycloak_authenticated');
      localStorage.removeItem('keycloak_refresh_token');
    };

    keycloak.onAuthError = () => {
    };

    // Proactive token refresh: Check every 30 seconds and refresh if < 60s left
    const refreshInterval = setInterval(() => {
      if (keycloak.authenticated && keycloak.tokenParsed?.exp) {
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = keycloak.tokenParsed.exp - now;
        
        if (timeUntilExpiry < 60 && timeUntilExpiry > 0) {
          keycloak.updateToken(70).catch(() => {
          });
        }
      }
    }, 30000); // Check every 30 seconds

    // Cleanup interval on unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const login = async (options) => {
    const redirectUri = options?.redirectUri || KEYCLOAK_REDIRECT_URI;
    
    const loginOptions = {
      redirectUri: redirectUri,
      idpHint: options?.idpHint || 'google',
      ...options,
    };
    
    await keycloak.login(loginOptions);
  };

  const logout = async (options) => {
    await keycloak.logout(options);
  };

  const updateToken = async (minValidity) => {
    return await keycloak.updateToken(minValidity);
  };

  const value = {
    keycloak,
    initialized,
    authenticated,
    token,
    user,
    login,
    logout,
    updateToken,
  };

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};
